import { wait } from '@idk-web/core-utils';
import {
  signInboxMessage,
  getSignInboxMessageResult,
  InboxSignResponse,
  EidProvider,
} from '@idk-web/api';

const POLL_INTERVAL_MS = 1000;
const POLL_TIMEOUT_MS = 10 * 60 * 1000;

type InitCallback = (
  data: Extract<InboxSignResponse, { status: 'IN_PROGRESS' }>,
) => void;
type CompleteCallback = () => void;
type ErrorCallback = (error: unknown) => void;
type ProgressCallback = (
  data: Extract<InboxSignResponse, { status: 'IN_PROGRESS' }>,
) => void;
type InitFunction = () => Promise<
  Extract<InboxSignResponse, { status: 'IN_PROGRESS' }>
>;
type CancelFunction = (id: string) => Promise<void>;
type GetStatusFunction = (id: string) => Promise<InboxSignResponse>;

export type PendingInboxSignRequest = {
  onInit(callback: InitCallback): PendingInboxSignRequest;
  onComplete(callback: CompleteCallback): PendingInboxSignRequest;
  onError(callback: ErrorCallback): PendingInboxSignRequest;
  onProgress(callback: ProgressCallback): PendingInboxSignRequest;
  start(): StartedInboxSignRequest;
};

export type StartedInboxSignRequest = {
  wait(): Promise<void>;
  cancel(): void;
};

export function bankIdSe(messageId: string): PendingInboxSignRequest {
  return startPolling(messageId, EidProvider.BANKID);
}

export function freja(messageId: string): PendingInboxSignRequest {
  return startPolling(messageId, EidProvider.FREJA);
}

export function bankIdNo(
  messageId: string,
  window: Window,
): PendingInboxSignRequest {
  return startExternal(messageId, EidProvider.BANKID_NO, window);
}

export function mitId(
  messageId: string,
  window: Window,
): PendingInboxSignRequest {
  return startExternal(messageId, EidProvider.MITID, window);
}

export function ftn(
  messageId: string,
  window: Window,
): PendingInboxSignRequest {
  return startExternal(messageId, EidProvider.FTN, window);
}

function startPolling(
  messageId: string,
  provider: EidProvider,
): PendingInboxSignRequest {
  const controller = new AbortController();

  return start(
    () => signInboxMessage(messageId, { provider }, controller.signal),
    async () => {
      // Not supported
    },
    (ref) => getSignInboxMessageResult(messageId, ref, controller.signal),
    controller,
  );
}

function startExternal(
  messageId: string,
  provider: EidProvider,
  window: Window,
): PendingInboxSignRequest {
  const controller = new AbortController();

  return start(
    async () => {
      const status = await signInboxMessage(
        messageId,
        { provider },
        controller.signal,
      );

      if (status.url) {
        window.location.href = status.url;
      }

      return status;
    },
    async () => window.close(),
    async (ref) => {
      if (window.closed) {
        const response = await getSignInboxMessageResult(
          messageId,
          ref,
          controller.signal,
        );

        if (controller.signal.aborted || response.status === 'IN_PROGRESS') {
          return { ...response, status: 'FAILED', error: 'cancelled', ref };
        }
      }

      return getSignInboxMessageResult(messageId, ref, controller.signal);
    },
    controller,
  );
}

function start(
  init: InitFunction,
  cancel: CancelFunction,
  getStatus: GetStatusFunction,
  controller: AbortController,
): PendingInboxSignRequest {
  const initCallbacks: InitCallback[] = [];
  const completeCallbacks: CompleteCallback[] = [];
  const errorCallbacks: ErrorCallback[] = [];
  const progressCallbacks: ProgressCallback[] = [];

  return {
    onInit(callback) {
      initCallbacks.push(callback);
      return this;
    },
    onComplete(callback) {
      completeCallbacks.push(callback);
      return this;
    },
    onError(callback) {
      errorCallbacks.push(callback);
      return this;
    },
    onProgress(callback) {
      progressCallbacks.push(callback);
      return this;
    },
    start() {
      const promise = run(
        init,
        cancel,
        getStatus,
        (data) => initCallbacks.forEach((f) => f(data)),
        () => completeCallbacks.forEach((f) => f()),
        (error) => errorCallbacks.forEach((f) => f(error)),
        (data) => progressCallbacks.forEach((f) => f(data)),
        controller,
      );

      return {
        wait: () => promise,
        cancel: () => controller.abort(),
      };
    },
  };
}

async function run(
  init: InitFunction,
  cancel: CancelFunction,
  getStatus: GetStatusFunction,
  onInit: InitCallback,
  onComplete: CompleteCallback,
  onError: ErrorCallback,
  onProgress: ProgressCallback,
  controller: AbortController,
): Promise<void> {
  let timeLeft = POLL_TIMEOUT_MS;
  let id: string;

  try {
    const response = await init();
    id = response.id;
    onInit(response);
  } catch (e) {
    onError(e);
    throw e;
  }

  controller.signal.addEventListener('abort', () => cancel(id));

  while (timeLeft > 0) {
    let response: InboxSignResponse;
    try {
      response = await getStatus(id);
    } catch (e: unknown) {
      onError(e);
      throw e;
    }

    if (controller.signal.aborted || timeLeft < 0) {
      break;
    }

    switch (response.status) {
      case 'FAILED':
        onError(response.error);
        throw new Error(response.error);
      case 'COMPLETED':
        onComplete();
        return;
    }

    timeLeft -= POLL_INTERVAL_MS;

    if (controller.signal.aborted || timeLeft < 0) {
      break;
    } else {
      onProgress(response);
    }

    await wait(POLL_INTERVAL_MS);

    if (controller.signal.aborted || timeLeft < 0) {
      break;
    }
  }

  if (controller.signal.aborted) {
    onError('cancelled');
    throw new Error('cancelled');
  } else {
    onError('timed out');
    throw new Error('timed out');
  }
}
